import { useCallback, useEffect } from 'react';
import { useFetcher } from 'react-router';

import { useHardRedirect } from '@jane/bloom/hooks/useHardRedirect';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type {
  Payload,
  SignupUserResponse,
} from '@jane/bloom/routes/api.signup/types';
import { trackRegistered } from '@jane/bloom/services/tracking/track';
import { buildFormData } from '@jane/bloom/utils/formData';

interface UseSignupArgs {
  /**
   * When provided, called on successful signup instead of performing a hard
   * redirect. Mirrors `useLogin` — lets the caller keep the user on the page
   * (loaders still revalidate) to open a follow-up modal.
   */
  onSuccess?: (result: { redirectTo: string }) => void;
  skipBagAfterAuth?: boolean;
}

export const useSignup = ({
  onSuccess,
  skipBagAfterAuth,
}: UseSignupArgs = {}) => {
  const { featureFlags, googleSsoClientId } = useRootLoaderData();
  const hardRedirect = useHardRedirect();
  const fetcher = useFetcher<SignupUserResponse>();

  const signup = useCallback(
    (args: Omit<Payload, 'skipBag'>) => {
      return fetcher.submit(
        buildFormData({
          ...args,
          skipBag: skipBagAfterAuth ? `${skipBagAfterAuth}` : undefined,
        }),
        {
          action: `/api/signup`,
          method: 'post',
          preventScrollReset: true,
        }
      );
    },
    [fetcher, skipBagAfterAuth]
  );

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data?.success) {
      trackRegistered(
        {
          googleSsoEnabled: !!googleSsoClientId,
          method: 'email',
          source: fetcher.data.payload.authSource,
        },
        featureFlags
      );
      if (onSuccess) {
        onSuccess({ redirectTo: fetcher.data.payload.redirectTo });
      } else {
        hardRedirect(fetcher.data.payload.redirectTo);
      }
    }
  }, [
    featureFlags,
    fetcher.state,
    fetcher.data,
    googleSsoClientId,
    hardRedirect,
    onSuccess,
  ]);

  return {
    fetcher,
    signup,
  };
};
